import { concatStyleSets, IStyle } from '@fluentui/react';

import shimmer from '../../assets/images/shimmer.gif';
import { IH2OTheme } from '../../models/components.models';
import { FontWeights, Sizes } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';
import { DetailsListSizes } from '../DetailsList/DetailsList.styles';

export interface IListRowStyles {
  root: IStyle;
  row: IStyle;
  button: IStyle;
  details: IStyle;
  title: IStyle;
  contents: IStyle;
  loaderForChildRows: IStyle;
}

export interface IListRowClassNames {
  root: string;
  row: string;
  button: string;
  details: string;
  title: string;
  contents: string;
  loaderForChildRows: string;
}

export enum ListRowSizes {
  ChildListHeaderHeight = 33,
  CardHeight = 72,
  Height = 48,
  ContentsHeight = 354,
  ContentsContentsHeight = 286,
  WidgetHeight = 69,
}

export const listRowStylesDefault = (theme: IH2OTheme): Partial<IListRowStyles> => ({
  root: {
    '&.has-left-open-close': {
      '.h2o-ListRow-button': {
        left: 24,
        right: 'auto',
      },
    },
    '&.row-group': {
      '.h2o-ListCell-root.is-first': {
        paddingLeft: 0,
      },
    },
    '&.is-loading': {
      '.h2o-ListRow-row': {
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% 1px',
        backgroundImage: `url(${shimmer})`,
      },
    },
    '&.is-clickable': {
      cursor: 'pointer',
    },
    '&.is-expanded': {
      '.h2o-ListRow-row:hover': {
        background: 'none',
      },
    },
    '&.is-disabled': {
      '.h2o-ListRow-row': {
        backgroundColor: theme.semanticColors?.buttonDisabledBackground,
        borderRadius: Sizes.borderRadius,
        color: theme.semanticColors?.textDisabled,
        '.h2o-ListCell-text, .h2o-ListCell-header': {
          color: theme.semanticColors?.textDisabled,
        },
      },
    },
  },
  row: {
    display: 'flex',
    position: 'relative',
    height: ListRowSizes.CardHeight,
    '&:hover': {
      borderRadius: Sizes.borderRadius,
      background: theme.palette?.primary100,
      '.h2o-ListCell-text': {
        color: theme.semanticColors?.textPrimary,
      },
    },
  },
  button: {
    position: 'absolute',
    height: '100%',
    right: 28,
    ...CommonStyles.flexCenterVertically,
  },
  details: {
    paddingLeft: DetailsListSizes.GroupPaddingLeftRight,
    paddingRight: DetailsListSizes.GroupPaddingLeftRight,
  },
  title: {
    borderTop: `2px solid ${theme.palette?.gray300}`,
    fontWeight: FontWeights.bold,
    paddingTop: 16,
    '&:hover': {
      borderColor: `${theme.palette?.gray300} !important`,
    },
  },
  contents: {},
  loaderForChildRows: {
    padding: 24,
  },
});

export const listRowStylesThin: Partial<IListRowStyles> = {
  row: {
    height: ListRowSizes.Height,
    '&:hover, &:active': {
      borderRadius: 0,
    },
  },
};

export const listRowStylesAutoHeight: Partial<IListRowStyles> = {
  row: {
    height: 'auto',
    '.h2o-ListCell-root': {
      display: 'block',
      paddingTop: 24,
      paddingBottom: 24,
    },
  },
};

export const listRowStylesContents: Partial<IListRowStyles> = {
  root: { height: ListRowSizes.ContentsHeight },
  row: {
    height: 40,
    '.h2o-ListCell-root:first-child': {
      paddingLeft: 0,
    },
    '&:hover': {
      background: 'transparent',
    },
  },
  details: {
    padding: 0,
    paddingTop: 28,
  },
};

export const listRowStylesCustomRow: Partial<IListRowStyles> = {
  root: {
    '&.is-loading': {
      '.h2o-ListRow-row': {
        borderTop: '1px solid white',
        borderRadius: 0,
      },
    },
  },
  row: {
    height: 'auto',
    minHeight: 56, // for loading background image
    '.h2o-ListCell-root': {
      display: 'block',
      padding: 0,
    },
    '&:hover': {
      background: 'transparent',
    },
  },
};

export const listRowStylesThinWithCellDivider = (theme: IH2OTheme): Partial<IListRowStyles> =>
  concatStyleSets(listRowStylesThin, {
    row: {
      '.h2o-ListCell-root': {
        borderLeft: `1px solid ${theme.palette?.gray200}`,
      },
      '.h2o-ListCell-root:first-child': {
        borderLeft: 0,
      },
    },
  });

export const listRowStylesWidgetLine = (theme: IH2OTheme): Partial<IListRowStyles> => ({
  root: {
    borderBottom: `1px solid ${theme.semanticColors?.inputBorder}`,
  },
  row: {
    height: ListRowSizes.WidgetHeight,
  },
});

export const listRowStylesThinWithBorder = (theme: IH2OTheme): Partial<IListRowStyles> =>
  concatStyleSets(listRowStylesThin, {
    row: {
      borderTop: `1px solid ${theme.palette?.gray200}`,
    },
  });
